import React, { useState, useMemo } from 'react';

const PRESET_BASE = [5, 10, 20];
const makePresets = (max) => {
    if (max <= 0) return [5];
    const base = PRESET_BASE.filter(p => p < max);
    return [...base, max];
};

const getScoreMeta = (pct) => {
    if (pct >= 85) return { color: 'text-emerald-300', label: 'High Pass' };
    if (pct >= 70) return { color: 'text-sky-300', label: 'Pass' };
    if (pct >= 60) return { color: 'text-amber-300', label: 'Borderline' };
    return { color: 'text-rose-400', label: 'Fail' };
};

const ExamCreateModal = ({
    allFolders = [],
    savedExams = [],
    examHistory = [],
    entitlements = {},
    preSelectedFolderId = null,
    onConfirm,
    onRetake,
    onViewHistory,
    onClose,
}) => {
    const [view, setView] = useState('create');
    const [selectedFolderIds, setSelectedFolderIds] = useState(() =>
        preSelectedFolderId ? { [preSelectedFolderId]: true } : {}
    );
    const [questionCount, setQuestionCount] = useState(10);
    const [explanationMode, setExplanationMode] = useState('now');
    const [timedMode, setTimedMode] = useState(false);
    const [flaggedOnly, setFlaggedOnly] = useState(false);

    const creditsBalance = entitlements?.creditsBalance ?? 0;
    const includedRemaining = entitlements?.includedExamsRemaining ?? 0;

    const selectedFolderList = allFolders.filter(f => selectedFolderIds[f.id]);
    const selectedCount = selectedFolderList.length;
    const totalSelectedCards = selectedFolderList.reduce((s, f) => s + (f.cards?.length ?? 0), 0);
    const totalSelectedFlaggedCards = selectedFolderList.reduce(
        (s, f) => s + (f.cards?.filter(c => c.isFlagged).length ?? 0), 0
    );
    const availableCardCount = flaggedOnly ? totalSelectedFlaggedCards : totalSelectedCards;

    const maxQ = Math.max(1, Math.min(availableCardCount || 50, 50));
    const presets = useMemo(() => makePresets(Math.min(maxQ, 50)), [maxQ]);
    const clamp = (n) => Math.max(1, Math.min(maxQ, Math.floor(isNaN(n) ? 1 : n)));
    const safeCount = clamp(questionCount);

    const creditCost = safeCount;
    const hasFreeExam = includedRemaining > 0;
    const canAfford = hasFreeExam || creditsBalance >= creditCost;
    const hasEnoughCards = !flaggedOnly || totalSelectedFlaggedCards > 0;
    const canStart = selectedCount > 0 && canAfford && hasEnoughCards;
    const estimatedMinutes = Math.ceil((safeCount * (timedMode ? 90 : 60)) / 60);

    const toggleFolder = (id) => setSelectedFolderIds(prev => ({ ...prev, [id]: !prev[id] }));
    const toggleAll = () => {
        if (selectedCount === allFolders.length) {
            setSelectedFolderIds({});
        } else {
            const all = {};
            allFolders.forEach(f => { all[f.id] = true; });
            setSelectedFolderIds(all);
        }
    };

    const handleConfirm = () => {
        if (!canStart) return;
        onConfirm({ questionCount: safeCount, explanationMode, timedMode, selectedFolderIds, flaggedOnly });
    };

    const recentAttempt = examHistory[0] ?? null;

    // ── Retake view ──────────────────────────────────────────────────────────
    if (view === 'retake') {
        return (
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-950/75 backdrop-blur-sm sm:px-4" onClick={onClose}>
                <div
                    className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col"
                    style={{ backgroundColor: '#0d1526', boxShadow: '0 32px 64px rgba(0,0,0,0.6)', maxHeight: 'calc(88dvh - env(safe-area-inset-bottom))' }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-teal-600/20 to-emerald-600/10" />
                        <div className="relative flex items-center gap-3 px-5 py-4 border-b border-white/10">
                            <button onClick={() => setView('create')} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="flex-1">
                                <h2 className="text-base font-semibold text-white">Retake Saved Exam</h2>
                                <p className="text-[11px] text-slate-400 mt-0.5">{savedExams.length} saved · questions reshuffled each attempt</p>
                            </div>
                            <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-3">
                        {savedExams.length === 0 ? (
                            <div className="flex flex-col items-center py-10 text-center">
                                <div className="h-12 w-12 rounded-2xl bg-slate-800/60 flex items-center justify-center mb-3">
                                    <svg className="h-6 w-6 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <p className="text-sm text-slate-400 font-medium">No saved exams yet</p>
                                <p className="text-xs text-slate-600 mt-1">Create an exam and save your score to see it here.</p>
                                <button onClick={() => setView('create')} className="mt-4 text-xs text-teal-400 hover:text-teal-300 transition">
                                    ← Create a new exam
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1.5">
                                {savedExams.map(exam => {
                                    const qCount = exam.config?.questionCount ?? exam.questions?.length ?? null;
                                    return (
                                        <button
                                            key={exam.id}
                                            onClick={() => onRetake(exam.id)}
                                            className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left bg-slate-900/50 ring-1 ring-white/8 hover:bg-slate-800/60 hover:ring-white/15 transition group"
                                        >
                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-500/15 text-teal-300 group-hover:bg-teal-500/25 transition">
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-200 truncate group-hover:text-white transition">{exam.title}</p>
                                                {qCount && <p className="text-[11px] text-slate-500 mt-0.5">{qCount} questions</p>}
                                            </div>
                                            <svg className="h-4 w-4 shrink-0 text-slate-600 group-hover:text-slate-400 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="px-5 py-4 border-t border-white/8 flex justify-between items-center" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
                        <button onClick={() => setView('create')} className="min-h-[44px] px-3 text-sm text-slate-400 hover:text-white transition">← Back</button>
                        <button onClick={onClose} className="min-h-[44px] inline-flex items-center rounded-xl px-4 py-2 text-sm text-slate-400 hover:text-white bg-slate-800/60 ring-1 ring-white/10 hover:bg-slate-800 transition">Close</button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Create view ───────────────────────────────────────────────────────────
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-950/75 backdrop-blur-sm sm:px-4" onClick={onClose}>
            <div
                className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col"
                style={{ backgroundColor: '#0d1526', boxShadow: '0 32px 64px rgba(0,0,0,0.6)', maxHeight: 'calc(92dvh - env(safe-area-inset-bottom))' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Mobile drag handle */}
                <div className="flex justify-center pt-2.5 pb-0.5 sm:hidden shrink-0">
                    <div className="w-10 h-1 rounded-full bg-white/20" />
                </div>

                {/* Header */}
                <div className="relative overflow-hidden shrink-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-600/25 to-emerald-700/15" />
                    <div className="relative flex items-center gap-3 px-5 py-4 border-b border-white/10">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 shadow-lg shadow-teal-500/25">
                            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-base font-semibold text-white">Flash Exam</h2>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                {hasFreeExam ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300 bg-emerald-500/15 rounded-full px-2 py-0.5">
                                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                        {includedRemaining} exam{includedRemaining !== 1 ? 's' : ''} included
                                    </span>
                                ) : (
                                    <span className="text-[11px] text-slate-500">
                                        <span className="text-slate-300 font-semibold tabular-nums">{creditsBalance}</span> credits available
                                    </span>
                                )}
                                {recentAttempt && (() => {
                                    const meta = getScoreMeta(recentAttempt.percentage ?? 0);
                                    return (
                                        <span className={`text-[11px] ${meta.color}`}>
                                            · Last: {recentAttempt.percentage}% ({meta.label})
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            {savedExams.length > 0 && (
                                <button
                                    onClick={() => setView('retake')}
                                    className="min-h-[36px] inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[11px] text-slate-300 hover:text-white bg-slate-800/60 ring-1 ring-white/10 hover:ring-white/20 transition"
                                >
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Retake
                                </button>
                            )}
                            {examHistory.length > 0 && (
                                <button
                                    onClick={() => onViewHistory?.()}
                                    className="min-h-[36px] inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[11px] text-slate-300 hover:text-white bg-slate-800/60 ring-1 ring-white/10 hover:ring-white/20 transition"
                                >
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    History
                                </button>
                            )}
                            <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto min-h-0">

                    {/* Marketing copy */}
                    <div className="mx-5 mt-4 mb-1 rounded-xl bg-gradient-to-r from-teal-600/10 to-emerald-600/8 ring-1 ring-teal-500/20 px-4 py-3">
                        <p className="text-[11px] text-slate-300 leading-relaxed">
                            Need extra questions to solidify a knowledge gap? Create a deck from your prep and craft a high-quality question set, direct from your materials. All questions contain peer-reviewed references and detailed explanations.
                        </p>
                    </div>

                    {/* Folder section */}
                    <div className="px-5 pt-4 pb-3">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Folders</p>
                            <div className="flex items-center gap-2">
                                {selectedCount > 0 && (
                                    <span className="text-[11px] text-slate-400">
                                        <span className="text-white font-semibold tabular-nums">{selectedCount}</span> selected
                                        {totalSelectedCards > 0 && <span className="text-slate-600"> · {totalSelectedCards} cards</span>}
                                    </span>
                                )}
                                {allFolders.length > 1 && (
                                    <button
                                        onClick={toggleAll}
                                        className="text-[11px] text-slate-500 hover:text-slate-300 transition"
                                    >
                                        {selectedCount === allFolders.length ? 'Deselect all' : 'Select all'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {allFolders.length === 0 ? (
                            <div className="rounded-xl bg-slate-900/50 ring-1 ring-white/8 px-4 py-6 text-center">
                                <p className="text-sm text-slate-500">No folders yet — create one to get started.</p>
                            </div>
                        ) : (
                            <div className="rounded-xl overflow-hidden ring-1 ring-white/8 max-h-44 overflow-y-auto">
                                {allFolders.map((folder, i) => {
                                    const checked = !!selectedFolderIds[folder.id];
                                    const cardCount = folder.cards?.length ?? 0;
                                    const isLast = i === allFolders.length - 1;
                                    return (
                                        <button
                                            key={folder.id}
                                            onClick={() => toggleFolder(folder.id)}
                                            className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition ${isLast ? '' : 'border-b border-white/6'} ${checked ? 'bg-teal-600/12' : 'bg-slate-900/60 hover:bg-slate-800/60'}`}
                                        >
                                            <div className={`h-4 w-4 shrink-0 rounded flex items-center justify-center transition ${checked ? 'bg-teal-500 ring-1 ring-teal-400' : 'bg-slate-800 ring-1 ring-white/15'}`}>
                                                {checked && (
                                                    <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className={`flex-1 text-sm truncate transition ${checked ? 'text-white font-medium' : 'text-slate-300'}`}>{folder.name}</span>
                                            {cardCount > 0 && (
                                                <span className="shrink-0 text-[11px] text-slate-600 tabular-nums">{cardCount}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {selectedCount > 0 && (
                        <div className="px-5 pb-3">
                            <div className={`flex items-center justify-between rounded-xl px-3.5 py-2.5 transition ${flaggedOnly ? 'bg-amber-500/10 ring-1 ring-amber-400/25' : 'bg-slate-900/40 ring-1 ring-white/8'}`}>
                                <div className="flex items-center gap-2.5">
                                    <div className={`h-7 w-7 rounded-lg flex items-center justify-center transition ${flaggedOnly ? 'bg-amber-500/25 text-amber-300' : 'bg-slate-800 text-slate-500'}`}>
                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16v12l-6-3-6 3V4z" /></svg>
                                    </div>
                                    <div>
                                        <p className={`text-sm font-medium transition ${flaggedOnly ? 'text-white' : 'text-slate-300'}`}>Flagged cards only</p>
                                        <p className="text-[11px] text-slate-500 mt-0.5">
                                            {totalSelectedFlaggedCards > 0
                                                ? `${totalSelectedFlaggedCards} flagged in selected folder${totalSelectedFlaggedCards !== 1 ? 's' : ''} — drill exactly what you flagged`
                                                : 'No flagged cards in the selected folders yet'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setFlaggedOnly(v => !v)}
                                    disabled={totalSelectedFlaggedCards === 0}
                                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${flaggedOnly ? 'bg-amber-500' : 'bg-slate-700'}`}
                                >
                                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${flaggedOnly ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mx-5 border-t border-white/6" />

                    {/* Question count */}
                    <div className="px-5 py-3">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Questions</p>
                            <span className="text-[11px] text-slate-600">1 credit each</span>
                        </div>
                        <div className="flex gap-1.5 mb-2.5">
                            {presets.map(p => {
                                const isActive = questionCount === p;
                                const label = !PRESET_BASE.includes(p) && p === Math.min(maxQ, 50) ? 'Max' : String(p);
                                return (
                                    <button
                                        key={p}
                                        onClick={() => setQuestionCount(p)}
                                        className={`flex-1 rounded-lg py-1.5 text-sm font-medium transition ${isActive ? 'bg-teal-600 text-white shadow-lg shadow-teal-500/20' : 'bg-slate-800/70 text-slate-300 ring-1 ring-white/10 hover:ring-white/20'}`}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex items-center gap-2 rounded-xl bg-slate-900/50 ring-1 ring-white/8 px-2 py-1">
                            <button
                                onClick={() => setQuestionCount(c => clamp(c - 1))}
                                className="h-7 w-7 flex items-center justify-center rounded-lg bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 transition text-lg font-light"
                            >−</button>
                            <input
                                type="number"
                                min={1}
                                max={maxQ}
                                value={questionCount}
                                onChange={e => setQuestionCount(clamp(Number(e.target.value)))}
                                className="flex-1 bg-transparent text-center text-sm font-semibold text-white focus:outline-none tabular-nums"
                            />
                            <button
                                onClick={() => setQuestionCount(c => clamp(c + 1))}
                                className="h-7 w-7 flex items-center justify-center rounded-lg bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 transition text-lg font-light"
                            >+</button>
                        </div>
                    </div>

                    <div className="mx-5 border-t border-white/6" />

                    {/* Options row */}
                    <div className="px-5 py-3 flex flex-col gap-3">
                        {/* Explanation mode */}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-300 font-medium">Explanations</p>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                    {explanationMode === 'now' ? 'Shown immediately after each answer' : 'Revealed at end — full simulation'}
                                </p>
                            </div>
                            <div className="flex rounded-lg overflow-hidden ring-1 ring-white/10 shrink-0 ml-4">
                                {[['now', 'Immediate'], ['later', 'After']].map(([val, label]) => (
                                    <button
                                        key={val}
                                        onClick={() => setExplanationMode(val)}
                                        className={`px-3 py-1.5 text-xs font-medium transition ${explanationMode === val ? 'bg-teal-600 text-white' : 'bg-slate-800/70 text-slate-400 hover:text-white'}`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Timed mode */}
                        <div className={`flex items-center justify-between rounded-xl px-3.5 py-2.5 transition ${timedMode ? 'bg-amber-500/10 ring-1 ring-amber-400/25' : 'bg-slate-900/40 ring-1 ring-white/8'}`}>
                            <div className="flex items-center gap-2.5">
                                <div className={`h-7 w-7 rounded-lg flex items-center justify-center transition ${timedMode ? 'bg-amber-500/25 text-amber-300' : 'bg-slate-800 text-slate-500'}`}>
                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className={`text-sm font-medium transition ${timedMode ? 'text-white' : 'text-slate-300'}`}>Timed Mode</p>
                                    <p className="text-[11px] text-slate-500">90 sec/question · board simulation</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setTimedMode(t => !t)}
                                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${timedMode ? 'bg-amber-500' : 'bg-slate-700'}`}
                            >
                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${timedMode ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-4 border-t border-white/8 shrink-0" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
                    {/* Credit / resource status */}
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-xs text-slate-500 leading-relaxed">
                            {selectedCount === 0 ? (
                                <span className="text-slate-600">Select at least one folder to continue</span>
                            ) : !hasEnoughCards ? (
                                <span className="text-rose-400">No flagged cards in the selected folders — flag some first, or turn this off</span>
                            ) : hasFreeExam ? (
                                <span>
                                    <span className="text-emerald-300 font-semibold">{includedRemaining} included exam{includedRemaining !== 1 ? 's' : ''}</span>
                                    <span className="text-slate-600"> · 1 will be used</span>
                                </span>
                            ) : !canAfford ? (
                                <span className="text-rose-400">Not enough credits ({creditsBalance} available, {creditCost} needed)</span>
                            ) : (
                                <span>
                                    <span className="text-slate-300 font-semibold tabular-nums">{creditCost}</span>
                                    <span className="text-slate-600"> credits · {estimatedMinutes} min · </span>
                                    <span className="text-slate-500 tabular-nums">{creditsBalance - creditCost} remaining</span>
                                </span>
                            )}
                        </div>
                        <span className="text-[10px] text-slate-600 shrink-0 ml-3">1 credit / question</span>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="inline-flex items-center rounded-xl px-4 py-2.5 text-sm text-slate-400 hover:text-white bg-slate-800/60 ring-1 ring-white/10 hover:bg-slate-800 transition"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={!canStart}
                            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition ${
                                canStart
                                    ? 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 shadow-teal-500/20'
                                    : 'bg-slate-700/60 text-slate-500 cursor-not-allowed'
                            }`}
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Start Exam
                            {selectedCount > 0 && safeCount > 0 && (
                                <span className="rounded-full bg-white/15 px-1.5 py-0.5 text-[11px] font-bold tabular-nums">{safeCount}Q</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExamCreateModal;
